import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { Typography, Box, Link } from '@mui/material';

const MarkdownRenderer = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      components={{
        p: ({ children, ...props }) => (
          <Typography
            variant='body1'
            {...props}
            sx={{
              lineHeight: 1.6,
              mb: 1,
            }}
          >
            {children}
          </Typography>
        ),
        h1: ({ children, ...props }) => (
          <Typography
            variant='h4'
            {...props}
            sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        h2: ({ children, ...props }) => (
          <Typography
            variant='h5'
            {...props}
            sx={{ mb: 1.5, mt: 1.5, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        h3: ({ children, ...props }) => (
          <Typography
            variant='h6'
            {...props}
            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        h4: ({ children, ...props }) => (
          <Typography
            variant='subtitle1'
            {...props}
            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        h5: ({ children, ...props }) => (
          <Typography
            variant='subtitle2'
            {...props}
            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        h6: ({ children, ...props }) => (
          <Typography
            variant='body2'
            {...props}
            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
          >
            {children}
          </Typography>
        ),
        ul: ({ children, ...props }) => (
          <Box component='ul' {...props} sx={{ pl: 2, mb: 1 }}>
            {children}
          </Box>
        ),
        ol: ({ children, ...props }) => (
          <Box component='ol' {...props} sx={{ pl: 2, mb: 1 }}>
            {children}
          </Box>
        ),
        li: ({ children, ...props }) => (
          <Box component='li' {...props} sx={{ mb: 0.5 }}>
            {children}
          </Box>
        ),
        table: ({ children, ...props }) => (
          <Box
            component='table'
            {...props}
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {children}
          </Box>
        ),
        th: ({ children, ...props }) => (
          <Box
            component='th'
            {...props}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              bgcolor: 'grey.100',
              fontWeight: 'bold',
              textAlign: 'left',
            }}
          >
            {children}
          </Box>
        ),
        td: ({ children, ...props }) => (
          <Box
            component='td'
            {...props}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
            }}
          >
            {children}
          </Box>
        ),
        code: ({ children, ...props }) => (
          <Box
            component='code'
            {...props}
            sx={{
              bgcolor: 'grey.100',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.875em',
            }}
          >
            {children}
          </Box>
        ),
        pre: ({ children, ...props }) => (
          <Box
            component='pre'
            {...props}
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 1,
              fontFamily: 'monospace',
            }}
          >
            {children}
          </Box>
        ),
        blockquote: ({ children, ...props }) => (
          <Box
            component='blockquote'
            {...props}
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              mb: 1,
              fontStyle: 'italic',
            }}
          >
            {children}
          </Box>
        ),
        a: ({ children, ...props }) => (
          <Link
            href={props.href}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            }}
            {...props}
          >
            {children}
          </Link>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;




